export interface SlotSummary { version?: number; stagedVersion?: number; assetType?: string; processed?: any }

export function slotStatus(s: SlotSummary): { text: string; tone: 'live' | 'staged' | 'none' } {
  if (s.stagedVersion) return { text: `Staged · v${s.stagedVersion}`, tone: 'staged' };
  if (s.version) return { text: `Live · v${s.version}`, tone: 'live' };
  return { text: 'Not set', tone: 'none' };
}

export interface PromoteGate {
  ok: boolean;
  /** Why the button is locked, or — when unlocked without canary proof — what is about to happen. */
  reason?: string;
  /** Promoting without a single canary device having confirmed the boot install. */
  unverified?: boolean;
}

/**
 * Whether "Promote to fleet" may run.
 *
 * Publish writes ONLY the canary manifest; promote is what copies it to
 * `branding-manifest.json`, the file the installer and every stable terminal
 * read. So until a promote happens the fleet has no branding at all.
 *
 * That made the original "every canary device must confirm the boot install"
 * rule a deadlock on a fresh device type: the installer and the app both
 * default to the stable channel, so no device is ever enrolled on canary, so
 * `canary.length === 0` locked promote forever and the very first boot
 * animation could never reach a terminal. Nobody could tell that apart from
 * "the video just doesn't work".
 *
 * Canary proof is therefore required only when canary devices actually exist.
 * With none enrolled there is nothing to wait for, so promote is allowed and
 * says plainly that it is going out unverified.
 */
export function canPromote(devices: any[], bootVersion?: number): PromoteGate {
  if (!bootVersion) {
    return { ok: false, reason: 'Nothing to promote — upload a boot animation and publish to canary first.' };
  }
  const canary = devices.filter((d) => d.assignedChannel === 'canary');
  if (canary.length === 0) {
    return {
      ok: true,
      unverified: true,
      reason: `No canary devices are enrolled, so there is nothing to soak-test against — promoting sends boot v${bootVersion} straight to the fleet.`,
    };
  }
  if (!canary.every((d) => d.bootInstalled === true && d.bootVersion === bootVersion)) {
    return { ok: false, reason: 'Promote unlocks when canary devices report the new boot installed.' };
  }
  return { ok: true };
}
